import type { ExhibitionRegistry, PublicExhibition } from "./contracts";
import type { Language, UiCopy } from "./i18n";

export type ExhibitionMenuHandlers = {
  onLanguage: (language: Language) => void;
  onSelect: (slug: string) => void;
};

export function renderExhibitionMenu(
  root: HTMLElement,
  registry: ExhibitionRegistry,
  language: Language,
  copy: UiCopy,
  handlers: ExhibitionMenuHandlers,
  focusSlug: string | null = null,
): void {
  root.innerHTML = `
    <main class="exhibition-index" aria-labelledby="exhibitions-title">
      <header class="exhibition-index__header">
        <p class="eyebrow">${copy.museumEyebrow}</p>
        <h1 id="exhibitions-title">${copy.exhibitionsTitle}</h1>
        <p>${copy.exhibitionsIntro}</p>
        <label class="language-picker">
          <span>${copy.languageLabel}</span>
          <select data-menu-language aria-label="${copy.languageLabel}">
            <option value="es"${language === "es" ? " selected" : ""}>ES</option>
            <option value="en"${language === "en" ? " selected" : ""}>EN</option>
          </select>
        </label>
      </header>
      <section class="exhibition-grid" aria-live="polite"></section>
    </main>`;
  root.querySelector<HTMLSelectElement>("[data-menu-language]")?.addEventListener("change", (event) => {
    const selector = event.currentTarget as HTMLSelectElement;
    handlers.onLanguage(selector.value === "en" ? "en" : "es");
  });
  const grid = required<HTMLElement>(root, ".exhibition-grid");
  if (registry.exhibitions.length === 0) {
    const empty = document.createElement("p");
    empty.className = "panel exhibition-empty";
    empty.textContent = copy.emptyExhibitions;
    grid.append(empty);
    return;
  }
  for (const exhibition of registry.exhibitions) {
    const card = createCard(exhibition, language, copy, handlers.onSelect);
    grid.append(card);
  }
  if (focusSlug !== null) {
    root.querySelector<HTMLButtonElement>(`button[data-exhibition-slug="${focusSlug}"]`)?.focus();
  }
}

export function renderUnknownExhibition(
  root: HTMLElement,
  copy: UiCopy,
  onBack: () => void,
): void {
  root.innerHTML = `
    <main class="exhibition-index exhibition-index--message">
      <section class="panel" role="alert">
        <p class="eyebrow">${copy.museumEyebrow}</p>
        <h1>${copy.notFoundTitle}</h1>
        <p>${copy.notFoundMessage}</p>
        <button class="primary" type="button" data-back>${copy.backToExhibitions}</button>
      </section>
    </main>`;
  required<HTMLButtonElement>(root, "[data-back]").addEventListener("click", onBack);
}

export function renderRegistryFailure(root: HTMLElement, copy: UiCopy, onRetry: () => void): void {
  root.innerHTML = `
    <main class="exhibition-index exhibition-index--message">
      <section class="panel" role="alert">
        <p class="eyebrow">${copy.fatalEyebrow}</p>
        <h1>${copy.fatalTitle}</h1>
        <p>${copy.genericFailure}</p>
        <button class="primary" type="button" data-retry>${copy.retry}</button>
      </section>
    </main>`;
  required<HTMLButtonElement>(root, "[data-retry]").addEventListener("click", onRetry);
}

function createCard(
  exhibition: PublicExhibition,
  language: Language,
  copy: UiCopy,
  onSelect: (slug: string) => void,
): HTMLElement {
  const localized = exhibition.localizations[language];
  const title = localized?.title ?? exhibition.title;
  const summary = localized?.summary ?? exhibition.summary;
  const article = document.createElement("article");
  article.className = "exhibition-card";

  const image = document.createElement("img");
  image.src = publicAssetUrl(exhibition.cover.path).href;
  image.alt = "";
  image.loading = "lazy";
  image.width = 960;
  image.height = 540;

  const body = document.createElement("div");
  body.className = "exhibition-card__body";
  const heading = document.createElement("h2");
  heading.textContent = title;
  const description = document.createElement("p");
  description.textContent = summary;
  const metadata = document.createElement("p");
  metadata.className = "exhibition-card__meta";
  metadata.textContent =
    language === "en"
      ? `${exhibition.room_count} room${exhibition.room_count === 1 ? "" : "s"} · ${exhibition.artwork_count} works`
      : `${exhibition.room_count} sala${exhibition.room_count === 1 ? "" : "s"} · ${exhibition.artwork_count} obras`;
  const button = document.createElement("button");
  button.type = "button";
  button.className = "primary";
  button.dataset.exhibitionSlug = exhibition.slug;
  button.textContent = copy.openExhibition;
  button.addEventListener("click", () => onSelect(exhibition.slug));
  article.addEventListener("click", (event) => {
    if (event.target !== button) button.click();
  });
  body.append(heading, description, metadata, button);
  article.append(image, body);
  return article;
}

function publicAssetUrl(path: string): URL {
  const base = new URL(import.meta.env.BASE_URL, window.location.href);
  const url = new URL(`generated/exhibitions/${path}`, base);
  if (url.origin !== window.location.origin) throw new Error("ASSET_ORIGIN_UNSAFE");
  return url;
}

function required<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`UI_ELEMENT_MISSING: ${selector}`);
  return element;
}
